import * as React from 'react';
import { updateSurvey, setSurveyLanguage, deleteSurveyTranslation, createSurveyTranslation, updateSurveyIntroduction } from '../../../../../../../api/surveyClientApi';
import { Flex } from '../../../../../../../designSystem/compositeComponents/FlexHelpers';
import { StartEndDateFields } from '../../../../../../../designSystem/inputs/StartEndDateFields';
import { updateActivity } from '../../../../../../../api/activityClientApi';
import { Activity, surveyHelper, fieldValidation } from '../../../../../../../amorg-common';
import { Survey } from '../../../../../../../amorg-common';
import { LanguageCode } from '../../../../../../../amorg-common';
import { Label } from '../../../../../../../designSystem/Label';
import { MessageCard } from '../../../../../../../designSystem/Message';
import { Modal } from '../../../../../../../designSystem/Modal';
import { Button } from '../../../../../../../designSystem/button/Button';
import { ButtonRow } from '../../../../../../../designSystem/button/ButtonRow';
import { OnOff } from '../../../../../../../designSystem/inputs/OnOff';
import { Card } from '../../../../../../../designSystem/Card';
import { FormField } from '../../../../../../../designSystem/formField/FormField';
import { FormFieldMessage } from '../../../../../../../designSystem/formField/FormFieldMessage';
import { ToggletipTheOne } from '../../../../../../../designSystem/ToggletipTheOne';
import { colors } from '../../../../../../../designSystem/colors/color';
import { ConfirmationModal } from '../../../../../../../designSystem/compositeComponents/ConfirmationButton';
import { EditableTextArea } from '../../../../../../../designSystem/compositeComponents/EditableTextArea';
import { SurveyStatusPanel } from './SurveyStatusPanel';

type SurveyInfoPanelProps = {
    activity:Activity,
    survey:Survey,
    selectedLanguageCode:LanguageCode,
    disableEdit:boolean,
    onSelectedLanguageCodeChanged:(code:LanguageCode)=>void,
    onSurveyUpdated:()=>void,
    onActivityUpdated:()=>void
}

export function SurveyInfoPanel(props:SurveyInfoPanelProps):JSX.Element
{
    const [dateError, setDateError] = React.useState(false);

    async function handleSaveDates(start: Date, end: Date) {
        const updateResult = await updateSurvey(
            props.survey.id, props.survey.title, start, end
        );
        if (!updateResult.isSuccess)
            setDateError(true);
        else {
            setDateError(false);
            props.onSurveyUpdated();
        }
    }

    async function handleSaveActivityDates(start: Date, end: Date) {
        const updateResult = await updateActivity(
            props.activity.id,
            props.activity.title,
            props.activity.description,
            start,
            end,
            props.activity.location
        );
        if(!updateResult.isSuccess)
        {
            setDateError(true);
        }
        if (updateResult.isSuccess){
            setDateError(false);
            props.onActivityUpdated();
        }
        return updateResult.isSuccess;
    }

    async function handleSaveIntroduction(introduction:string) {
        await updateSurveyIntroduction(
            props.survey.id,
            introduction,
            props.selectedLanguageCode
        );
        props.onSurveyUpdated();
    }

    const introText = props.selectedLanguageCode === props.survey.languageCode
        ? props.survey.introduction.text
        : props.survey.introduction.translations.find(t => t.languageCode === props.selectedLanguageCode)?.text || '';

    return (
        <Flex flexDirection="column" gap={16} style={{flexGrow:1}}>
            {!dateError && 
                <SurveyStatusPanel survey={props.survey} />
            }

            {dateError &&
                <MessageCard variant="error">Du har valgt en ugyldig kombination af datoer. Besvarelsesperioden for spørgeskemaet skal ligge indenfor APV&apos;ens aktivitetsperiode</MessageCard>
            }

            <Card variant="outlined">
                <Flex flexDirection="column" gap={32} style={{flexGrow:1}}>

                    <Flex justifyContent="space-between" alignItems="center" gap={16}>

                        <Flex flexDirection="column" gap={16} style={{flexGrow:1}}>
                            
                            <FormField
                                label="Periode for spørgeskemaets besvarelse"
                                inputComponent={
                                    <StartEndDateFields start={props.survey.start} end={props.survey.end} 
                                        onChanged={handleSaveDates} />
                                }
                            />

                            <ActivityDateEditor
                                activity={props.activity}
                                disableEdit={props.disableEdit}
                                onSave={handleSaveActivityDates}
                            />
                            
                        </Flex>

                        <LanguageSelectionPanel
                            survey={props.survey}
                            selectedLanguageCode={props.selectedLanguageCode}
                            disableEdit={props.disableEdit}
                            onSelectedLanguageCodeChanged={props.onSelectedLanguageCodeChanged}
                            onSurveyUpdated={props.onSurveyUpdated}
                        />

                    </Flex>

                    {!props.disableEdit &&introText.length > 1000 && 
                        <MessageCard variant="warning">
                            Du overskrider nu 1.000 ({introText.length}) tegn i din beskrivelse. Forskning viser, at meget lange tekster kan have en negativ effekt på gennemførelsen af spørgeskemaer/APV’er. Vi anbefaler, at du holder dig under 1.000 tegn, hvis det er muligt.
                        </MessageCard>
                    }
                    <FormField
                        label='Introduktion til respondenten'
                        inputComponent={
                            <EditableTextArea
                                value={introText}
                                onSave={(val) =>
                                {
                                    if (fieldValidation.survey.introductionText(val))
                                    {
                                        handleSaveIntroduction(val);
                                    }
                                } }
                                validator={(currentValue) =>
                                {
                                    return fieldValidation.survey.introductionText(currentValue)
                                        ? { valid: true }
                                        : { valid: false, errorMessage: `Ugyldig titel. Skal udfyldes med maks. 2000 (${currentValue.length}) tegn.` };
                                } }
                                disabled={props.disableEdit} ariaLabel={''} placeholder={''}
                            />
                        }
                    />

                </Flex>
            </Card>

        </Flex>
    );
}

type ActivityDateEditorProps = {
    activity: Activity,
    disableEdit: boolean,
    onSave: (start: Date, end: Date) => Promise<boolean>
}

function ActivityDateEditor(props: ActivityDateEditorProps): JSX.Element {
    const [showModal, setShowModal] = React.useState(false);
    const [draft, setDraft] = React.useState<{ start: Date, end: Date }>({
        start: props.activity.start,
        end: props.activity.end
    });

    function openModal() {
        setDraft({
            start: props.activity.start,
            end: props.activity.end
        });
        setShowModal(true);
    }

    async function saveModal() {
        const isSuccess = await props.onSave(draft.start, draft.end);
        if (isSuccess) {
            setShowModal(false);
        }
    }

    return (
        <>
            <FormFieldMessage style={{display:'flex', alignItems:'center'}}>
                <ToggletipTheOne icon='help' iconColor={colors.icon.active} horizontalAlignment='left'>
                    Perioden for APV&apos;ens gennemførelse bør indeholde tid til udearbejdelse af spørgeskemaet, besvarelse af spørgeskemaet og efterfølgende udarbejdning og behandling af handleplaner.
                    Besvarelsesperioden for spørgeskemaet skal derfor ligge indenfor den valgte APV-periode.
                </ToggletipTheOne>

                Periode for APV&apos;ens gennemførelse: {props.activity.start.toLocaleDateString('da-DK')} – {props.activity.end.toLocaleDateString('da-DK')}
                
                <Flex alignItems="center" gap={8}>
                    <Button
                        variant="text"
                        icon="interface_actions_edit"
                        onClick={props.disableEdit ? undefined : openModal}
                        ariaLabel="Rediger besvarelsesperiode"
                        disabled={props.disableEdit}
                    />
                </Flex>
            </FormFieldMessage>



            <Modal
                title="Rediger APV'ens perioden for gennemførelse"
                showModal={showModal}
                onCancel={() => setShowModal(false)}
                content={
                    <Flex flexDirection="column" gap={16}>
                        <StartEndDateFields
                            start={draft.start}
                            end={draft.end}
                            onChanged={(start, end) => setDraft({ start, end })}
                        />
                        <ButtonRow>
                            <Button variant="text" text="Annuller" onClick={() => setShowModal(false)} />
                            <Button variant="filled" text="Gem" onClick={saveModal} />
                        </ButtonRow>
                    </Flex>
                }
            />
        </>
    );
}

type LanguageSelectionPanelProps = {
    survey: Survey,
    selectedLanguageCode: LanguageCode,
    disableEdit: boolean,
    onSelectedLanguageCodeChanged: (code: LanguageCode) => void,
    onSurveyUpdated: () => void,
}

function LanguageSelectionPanel(props: LanguageSelectionPanelProps): JSX.Element 
{
    const [pendingAction, setPendingAction] = React.useState<{ mode: 'add' | 'remove', code: LanguageCode } | undefined>(undefined);
    
    const supportedLanguages = surveyHelper.getValidLanguageCodes(props.survey);

    async function addLanguage(code: LanguageCode) 
    {
        if(props.survey.languageCode !== code && supportedLanguages.find(l => l === code) === undefined)
        {
            await createSurveyTranslation(props.survey.id, code);
            props.onSurveyUpdated();
        }
    }

    async function removeLanguage(code: LanguageCode) 
    {
        if(props.survey.languageCode !== code && supportedLanguages.find(l => l === code) !== undefined)
        {
            await deleteSurveyTranslation(props.survey.id, code);
            props.onSurveyUpdated();
        }
    }

    async function setDefaultLanguage(code: LanguageCode)
    {
        if(props.survey.languageCode !== code && supportedLanguages.find(l => l === code) !== undefined)
        {
            await setSurveyLanguage(props.survey.id, code);
            props.onSurveyUpdated();
        }
    }

    async function setActiveLanguage(code: LanguageCode)
    {
        if(props.selectedLanguageCode !== code && supportedLanguages.find(l => l === code) !== undefined)
        {
            props.onSelectedLanguageCodeChanged(code);
        }
    }

    return (
        <Flex flexDirection="column" gap={4} width="400px">
            <Label>Sprogunderstøttelse</Label>
            <Flex flexDirection="column" gap={8}>
                {(['da', 'en'] as LanguageCode[]).map((code) => {

                    const languageName = code === 'da' ? 'Dansk' : 'Engelsk';
                    
                    const isSupported = supportedLanguages.find(l => l === code) !== undefined;
                    const isDefault = props.survey.languageCode === code;
                    const isActive = props.selectedLanguageCode === code;

                    return (
                        <Card key={code} variant={isSupported ? 'outlined' : 'lightGray'} padding="0.75rem 1rem">
                            <Flex justifyContent="space-between" alignItems="center" gap={16}>
                                <Flex flexDirection="column" gap={4}>
                                    <strong>{languageName}</strong>
                                </Flex>

                                {isActive
                                    ? <Button variant="filled" text="aktiv" />
                                    : <Button variant="outlined" text="vælg" onClick={() => setActiveLanguage(code)} disabled={!isSupported} />
                                }

                                <Flex alignItems="center" gap={12}>
                                    <OnOff
                                        active={isSupported}
                                        disabled={props.disableEdit || isDefault}
                                        onClick={(active) => active ? setPendingAction({ mode: 'add', code }) : setPendingAction({ mode: 'remove', code })} />

                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <input
                                            type="radio"
                                            name="default-language"
                                            checked={isDefault}
                                            disabled={props.disableEdit || !isSupported}
                                            onChange={() => setDefaultLanguage(code)}
                                        />
                                        Standard
                                    </label>
                                </Flex>
                            </Flex>
                        </Card>
                    );
                })}
            </Flex>

            <ConfirmationModal
                title={pendingAction?.mode === 'add' ? 'Tilføj sprogversion' : 'Fjern sprogversion'}
                showModal={pendingAction !== undefined}
                onCancel={() => setPendingAction(undefined)}
                onConfirm={async () => {
                    if (pendingAction?.mode === 'add') {
                        await addLanguage(pendingAction.code);
                    } else if (pendingAction?.mode === 'remove') {
                        await removeLanguage(pendingAction.code);
                    }
                    setPendingAction(undefined);
                }}
                content={
                    <div>
                        {pendingAction?.mode === 'add'
                            ? 'Er du sikker på, at du vil tilføje understøttelse af dette sprog?'
                            : 'Er du sikker på, at du vil fjerne understøttelse af dette sprog? Alle tekster på dette sprog vil blive fjernet.'}
                    </div>
                }
                requireKey={false}
                buttonText={pendingAction?.mode === 'add' ? 'Tilføj' : 'Fjern'}
            />
        </Flex>
    );
}